import AddIcon from "@mui/icons-material/Add";
import RefreshOutlined from "@mui/icons-material/RefreshOutlined";
import WarningAmber from "@mui/icons-material/WarningAmber";
import WorkOutlined from "@mui/icons-material/WorkOutlined";
import { Box, IconButton, type SxProps } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, Fragment, useMemo, useState } from "react";
import { NormalizedRequestApprovalDialog } from "@/components/approvalDialogs/RequestsApproval/NormalizedRequestApprovalDialog";
import { InHouseTooltip } from "@/components/InHouseTooltip/InHouseTooltip";
import { MedicalDepartmentAdditionsPopup } from "@/components/MedicalDepartmentAdditionsPopup/MedicalDepartmentAdditionsPopup";
import { useRequestsContext } from "@/contexts/RequestsContext";
import { useRoleColors } from "@/hooks/useRoleColors";
import { useSchedulePersonName } from "@/hooks/useSchedulePersonName";
import { blockedCellSx, emptyGridShiftCellStyle, getShiftCellStyle, gridRowHeight } from "@/pages/MasterSchedule/v2/utils";
import { requestApprovalMultiSizePopupMedicalDepartmentKey } from "@/services/LocalStorageService";
import SystemButton from "@/ui/SystemButton/SystemButton";
import { configuredAmount } from "@/utils/censusResolution";
import {
	type DepartmentLocation,
	type NurseInShift,
	type OpenShiftRequest,
	type ReplaceShiftRequest,
	RequestResponsesStatusOptions,
	RequestStatus,
	type TimeOffRequest,
} from "@/utils/models";
import { SynergyNurseInShiftMenu } from "../SynergyNurseInShiftMenu";
import { isDayBlockedForLocation, synergyNurseCellStyle, unassignedLocation } from "../utils";
import { ActiveTimeOffRequest } from "./ActiveTimeOffRequest/ActiveTimeOffRequest";

type Props = {
	nurses: NurseInShift[];
	date: DateTime;
	facility: DepartmentLocation;
	credentialedNurseIds: number[];
	timeOffRequests: TimeOffRequest[];
	refetchDayOffRequests?: () => Promise<void>;
	refetchDayOffRequestsLoading?: boolean;
	selectedNurseId?: number | null;
};

const slotStyle = {
	display: "flex",
	width: "100%",
	alignItems: "center",
	p: 0,
	height: "fit-content",
	justifyContent: "center",
	textWrap: "wrap",
	"&:last-child": {
		borderBottom: "none",
	},
} as const satisfies SxProps;

const smallIconSize = "16px";

export const SynergyNurseCell: FC<Props> = ({
	nurses: nursesRaw,
	date,
	facility,
	credentialedNurseIds,
	timeOffRequests,
	refetchDayOffRequests,
	refetchDayOffRequestsLoading,
	selectedNurseId,
}) => {
	const roleColor = useRoleColors();
	const personName = useSchedulePersonName();
	const nurses = selectedNurseId != null ? nursesRaw.filter((n) => n.nurseId === selectedNurseId) : nursesRaw;
	const [pendingRequestToRender, setPendingRequestToRender] = useState<OpenShiftRequest | ReplaceShiftRequest | null>(null);
	const { staffRequests } = useRequestsContext();
	const [clickedNurseForMenu, setClickedNurseForMenu] = useState<{ nurse: NurseInShift; anchor: HTMLElement } | null>(null);
	const [addMenuOpen, setAddMenuOpen] = useState(false);
	const isBlocked = isDayBlockedForLocation(date, facility);
	const dayAbbr = date.setLocale("en").toFormat("ccc").toLowerCase();
	const dayAmount = configuredAmount(facility, dayAbbr);
	// DEFAULT: a day whose target is 0 still draws one empty slot, so the cell has somewhere to place staff.
	const numSlots = dayAmount || 1;

	const getNurseTimeOffRequests = (nurseId: number) => {
		return timeOffRequests.filter((request) => request.requestingStaffId === nurseId);
	};

	const timeOffRequestInThisDate = (requests: TimeOffRequest[], date: DateTime) => {
		return requests.find((request) => date >= request.startDate.startOf("day") && date <= request.endDate.endOf("day"));
	};

	const openShiftsForCell = useMemo(() => {
		// Filter open shifts for this cell's location and date
		return staffRequests.filter((request) => {
			const locationMatches = request.location === facility.location;
			const requestDate = request.message.shift_start;
			const dateMatches = requestDate.hasSame(date, "day");
			return locationMatches && dateMatches;
		});
	}, [staffRequests, facility.location, date]);

	const requestsWithPendingNurses = useMemo(() => {
		return openShiftsForCell.filter((req) => req.message.request_data.filter((reqData) => reqData.status === RequestResponsesStatusOptions.PENDING).length > 0);
	}, [openShiftsForCell]);

	const activeRequests = useMemo(() => {
		return openShiftsForCell.filter((req) => req.status === RequestStatus.Active);
	}, [openShiftsForCell]);

	const activeRequestsRolesSet = useMemo(() => {
		return Array.from(new Set(activeRequests.flatMap((req) => req.message.roles))).join(", ");
	}, [activeRequests]);

	const handleRequestDialogClose = () => {
		setPendingRequestToRender(null);
	};

	const toggleAddMenu = () => {
		setAddMenuOpen((prev) => !prev);
	};

	if (isBlocked) {
		return (
			<Box sx={synergyNurseCellStyle}>
				{Array.from({ length: numSlots }, (_, index) => (
					<Box key={`blocked-${index}`} sx={{ ...slotStyle, ...blockedCellSx }} />
				))}
			</Box>
		);
	}

	const hasOpenShifts = activeRequests.length > 0 || requestsWithPendingNurses.length > 0;
	const slotsToRender = Math.max(numSlots, nurses.length);

	// If we have nurses and open shifts, we need an extra slot for the open shifts indicator
	const totalSlotsToRender = hasOpenShifts && nurses.length > 0 && dayAmount <= nurses.length ? slotsToRender + 1 : slotsToRender;

	return (
		<Fragment>
			{clickedNurseForMenu && (
				<SynergyNurseInShiftMenu
					nurseInShift={clickedNurseForMenu.nurse}
					anchorEl={clickedNurseForMenu.anchor}
					open={Boolean(clickedNurseForMenu)}
					onClose={() => setClickedNurseForMenu(null)}
				/>
			)}
			{!!pendingRequestToRender && (
				<NormalizedRequestApprovalDialog
					open={true}
					shouldNotAllowToMaximize={true}
					request={pendingRequestToRender}
					onClose={handleRequestDialogClose}
					popupLocalStorageKey={requestApprovalMultiSizePopupMedicalDepartmentKey}
				/>
			)}
			<Box sx={synergyNurseCellStyle}>
				{addMenuOpen && (
					<MedicalDepartmentAdditionsPopup
						key={`synergy-shift-addition-${date.toFormat("yyyy-MM-dd")}-${facility.id}`}
						mode={"add-single"}
						clickedDate={date}
						facility={facility}
						onClose={toggleAddMenu}
					/>
				)}
				{Array.from({ length: totalSlotsToRender }, (_, index) => {
					const nurse = nurses[index];
					const isLastSlot = index === totalSlotsToRender - 1;
					const shouldShowOpenShiftsIndicator = hasOpenShifts && ((nurses.length === 0 && index === 0) || (nurses.length > 0 && isLastSlot));

					if (nurse) {
						const isCredentialed = credentialedNurseIds.includes(nurse.nurseId);
						const foundRequests = getNurseTimeOffRequests(nurse.nurseId);
						const requestInDate = timeOffRequestInThisDate(foundRequests, date);
						return (
							<SystemButton
								buttonVariant={"borderless"}
								key={`nurse-${nurse.id}-${index}`}
								sx={slotStyle}
								onClick={(event) => {
									setClickedNurseForMenu({ nurse, anchor: event.currentTarget });
								}}
							>
								<Box sx={{ ...getShiftCellStyle(false, false), gap: 0.25 }}>
									<InHouseTooltip title={nurse.role}>
										<Box
											sx={{
												background: roleColor(nurse.role).dot,
												width: 8,
												height: 8,
												borderRadius: "50%",
												marginRight: 1,
											}}
										/>
									</InHouseTooltip>
									{personName(nurse.nurseId)}
									{!!nurse.recurringShiftId && (
										<InHouseTooltip title={"Recurring Shift"}>
											<RefreshOutlined fontSize={"inherit"} />
										</InHouseTooltip>
									)}
									{!isCredentialed && facility.location !== unassignedLocation.location && (
										<InHouseTooltip title="This staff member is not credentialed">
											<WarningAmber fontSize={"inherit"} sx={{ color: "#FFA726" }} />
										</InHouseTooltip>
									)}
									{!!requestInDate && (
										<ActiveTimeOffRequest
											timeOffRequestsOfSpecificStaff={foundRequests}
											dateSpecificRequest={requestInDate}
											refetchRequests={refetchDayOffRequests}
											refetchDayOffRequestsLoading={refetchDayOffRequestsLoading}
										/>
									)}
								</Box>
							</SystemButton>
						);
					}

					return (
						<Box
							key={`add-${index}`}
							sx={{
								...emptyGridShiftCellStyle,
								display: "flex",
								flexDirection: "row",
								alignItems: "center",
								height: gridRowHeight,
								justifyContent: "center",
								gap: 1,
							}}
						>
							<IconButton
								onClick={toggleAddMenu}
								size="small"
								sx={{
									color: "inherit",
								}}
							>
								<AddIcon fontSize={"inherit"} />
							</IconButton>
							{shouldShowOpenShiftsIndicator && activeRequests.length > 0 && (
								<InHouseTooltip
									title={`${activeRequests.length} open shift${activeRequests.length > 1 ? "s" : ""} for this location on ${date.toFormat("MMM dd")}, for roles: ${activeRequestsRolesSet}`}
								>
									<WorkOutlined
										sx={{
											color: "#1976d2",
											fontSize: smallIconSize,
										}}
									/>
								</InHouseTooltip>
							)}
							{shouldShowOpenShiftsIndicator &&
								requestsWithPendingNurses.length > 0 &&
								requestsWithPendingNurses.map((currentRequest) => {
									const pendingReqData = currentRequest.message.request_data.filter((reqData) => reqData.status === RequestResponsesStatusOptions.PENDING);
									return (
										<InHouseTooltip
											key={currentRequest.id}
											title={`${pendingReqData.length} pending request${pendingReqData.length > 1 ? "s" : ""} from staff members for this location on ${date.toFormat("MMM dd")}`}
										>
											<IconButton
												size={"small"}
												onClick={(_) => {
													setPendingRequestToRender(currentRequest);
												}}
											>
												<WorkOutlined
													sx={{
														color: "#ff9800",
														fontSize: smallIconSize,
													}}
												/>
											</IconButton>
										</InHouseTooltip>
									);
								})}
						</Box>
					);
				})}
			</Box>
		</Fragment>
	);
};
